
import { createContext, useContext } from "react";
import { Session, User } from "@supabase/supabase-js";
import { useAuthState } from "@/hooks/use-auth-state";
import { useAuthActions, UserData } from "@/hooks/use-auth-actions";

type AuthContextType = {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, userData: UserData) => Promise<void>;
  signOut: () => Promise<void>;
  setUserAsAdmin: () => Promise<boolean>;
  forceRefresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { 
    session, 
    user, 
    isLoading: stateLoading, 
    isAdmin, 
    forceRefresh 
  } = useAuthState();
  
  const { 
    signIn, 
    signUp, 
    signOut, 
    setUserAsAdmin: setUserAdminStatus, 
    isLoading: actionLoading 
  } = useAuthActions();

  // Combine loading states - if either state management or actions are loading
  const isLoading = stateLoading || actionLoading;

  // Wrapper for setUserAsAdmin to use the current user
  const setUserAsAdmin = async () => {
    if (!user) return false;
    const result = await setUserAdminStatus(user.id);
    if (result) {
      // If admin status was successfully set, refresh auth state
      await forceRefresh();
    }
    return result;
  };

  const value = {
    session,
    user,
    isLoading,
    isAdmin,
    signIn,
    signUp,
    signOut,
    setUserAsAdmin,
    forceRefresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
